import { lazy } from 'react';
import * as v from 'valibot';

import { DeepKeys } from '@tanstack/react-form';

import { formOpts } from '#form';

import {
	ClaimBody,
	ClaimValues,
	SingleClaim,
} from '@repo/api/claim/claims.types';
import { defaultTimeType } from '@repo/api/common.types';

export type Page = 'insured' | 'interruption' | 'policy' | 'review';

export const pageMap: { [key in Page]?: Page } = {
	insured: 'interruption',
	interruption: 'policy',
	policy: 'review',
};

export const pageTitles: { [key in Page]: string } = {
	insured: 'Insured Details',
	interruption: 'Interruption Details',
	policy: 'Policy Details',
	review: 'FNOL Summary',
};

export const fieldPages: { [key in DeepKeys<ClaimValues>]: Page } = {
	claimInsured: 'insured',
	'claimInsured.claimReferenceNumber': 'insured',
	'claimInsured.naicsCodeId': 'insured',
	'claimInsured.businessName': 'insured',
	'claimInsured.storeNumber': 'insured',
	'claimInsured.addressLine1': 'insured',
	'claimInsured.addressLine2': 'insured',
	'claimInsured.town': 'insured',
	'claimInsured.stateId': 'insured',
	'claimInsured.regionId': 'insured',
	'claimInsured.countryId': 'insured',
	'claimInsured.zipCode': 'insured',
	'claimInsured.name': 'insured',
	'claimInsured.email': 'insured',
	'claimInsured.phoneNumber': 'insured',
	'claim.hasAlternativeContact': 'insured',
	alternativeContact: 'insured',
	'alternativeContact.alternativeContactName': 'insured',
	'alternativeContact.alternativeContactEmail': 'insured',
	'alternativeContact.alternativeContactNumber': 'insured',
	'alternativeContact.isPrimaryContact': 'insured',
	'alternativeContact.relationshipToInsured': 'insured',
	claim: 'interruption',
	'claim.dateofLoss': 'interruption',
	'claim.incidentDescription': 'interruption',
	'claim.isCat': 'interruption',
	'claim.interruptionOnGoing': 'interruption',
	'claim.estimatedPeriodofIndemnity': 'interruption',
	'claim.estimatedPeriodofIndemnityDateType': 'interruption',
	'claim.calculation': 'interruption',
	'claim.catCodeId': 'interruption',
	'claim.causeOfLossCodeId': 'interruption',
	claimPolicy: 'policy',
	'claimPolicy.policyId': 'policy',
	'claimPolicy.policyNumber': 'policy',
	'claimPolicy.businessInterruptionLimit': 'policy',
	'claimPolicy.biLimit': 'policy',
	'claimPolicy.coInsurance': 'policy',
	'claimPolicy.percentage': 'policy',
	'claimPolicy.isMaximumLengthOfIndemnityUnlimited': 'policy',
	'claimPolicy.maximumLengthOfIndemnity': 'policy',
	'claimPolicy.waitingPeriod': 'policy',
	'claimPolicy.waitingPeriodDuration': 'policy',
	'claimPolicy.waitingPeriodDurationHours': 'policy',
	'claimPolicy.ordinaryPayrollInsured': 'policy',
	'claimPolicy.timeLimitApplied': 'policy',
	'claimPolicy.timeLimitNumber': 'policy',
	'claimPolicy.timeLimitDurationDays': 'policy',
	'claimPolicy.ordinaryPayrollAmounts': 'policy',
	'claimPolicy.dollarLimit': 'policy',
	'claimPolicy.isBusinessInterruptionSumInsured': 'policy',
	'claimPolicy.businessInterruptionSumInsured': 'policy',
	'claimPolicy.hasAverageClausePerPolicy': 'policy',
	'claimPolicy.isDeclarationLinkedPolicy': 'policy',
	'claimPolicy.organisationId': 'policy',
} as const;

export function isValidPage(page: string): page is Page {
	return ['insured', 'interruption', 'policy', 'review'].includes(page);
}

export function postData(
	values: ClaimValues,
	claimId: number,
	isDraft: boolean,
): ClaimBody {
	const claimPolicy = values.claimPolicy;

	return {
		isDraft,
		claimInsured: values.claimInsured,
		alternativeContact:
			values.claim.hasAlternativeContact ? values.alternativeContact : {},
		claim: {
			claimId,
			hasAlternativeContact: values.claim.hasAlternativeContact,

			dateofLoss:
				values.claim.dateofLoss ? values.claim.dateofLoss.split('T')[0] : null,
			incidentDescription: values.claim.incidentDescription,
			isCat: values.claim.isCat,
			interruptionOnGoing: values.claim.interruptionOnGoing,
			estimatedPeriodofIndemnity: values.claim.estimatedPeriodofIndemnity,
			estimatedPeriodofIndemnityDateType:
				values.claim.estimatedPeriodofIndemnityDateType ?? defaultTimeType,
			calculation: values.claim.calculation,
			catCodeId: values.claim.catCodeId,
			causeOfLossCodeId: values.claim.causeOfLossCodeId,
		},
		claimPolicy:
			claimPolicy == null ? null : (
				{
					...claimPolicy,
					policyId: claimPolicy.policyId === -1 ? null : claimPolicy.policyId,
					biLimit:
						claimPolicy.businessInterruptionLimit ? claimPolicy.biLimit : null,
					percentage: claimPolicy.coInsurance ? claimPolicy.percentage : null,
					maximumLengthOfIndemnity:
						!claimPolicy.isMaximumLengthOfIndemnityUnlimited ?
							claimPolicy.maximumLengthOfIndemnity
						:	null,
					waitingPeriodDuration:
						claimPolicy.waitingPeriod ?
							claimPolicy.waitingPeriodDuration
						:	null,
					waitingPeriodDurationHours:
						claimPolicy.waitingPeriod ?
							(claimPolicy.waitingPeriodDurationHours ?? true)
						:	null,
					timeLimitApplied:
						claimPolicy.ordinaryPayrollInsured ?
							claimPolicy.timeLimitApplied
						:	null,
					timeLimitNumber:
						claimPolicy.ordinaryPayrollInsured && claimPolicy.timeLimitApplied ?
							claimPolicy.timeLimitNumber
						:	null,
					timeLimitDurationDays:
						claimPolicy.ordinaryPayrollInsured && claimPolicy.timeLimitApplied ?
							(claimPolicy.timeLimitDurationDays ?? true)
						:	null,
					ordinaryPayrollAmounts:
						claimPolicy.ordinaryPayrollInsured ?
							claimPolicy.ordinaryPayrollAmounts
						:	null,
					dollarLimit:
						(
							claimPolicy.ordinaryPayrollInsured &&
							claimPolicy.ordinaryPayrollAmounts
						) ?
							claimPolicy.dollarLimit
						:	null,
					isDeclarationLinkedPolicy:
						claimPolicy.hasAverageClausePerPolicy ?
							claimPolicy.isDeclarationLinkedPolicy
						:	null,
					businessInterruptionSumInsured:
						claimPolicy.isBusinessInterruptionSumInsured ?
							claimPolicy.businessInterruptionSumInsured
						:	null,
				}
			),
	};
}

export const getValues = (data: SingleClaim): ClaimValues => {
	const alternativeContact = data.alternativeContact ?? {};

	let claimPolicy = data.claimPolicy;
	const policyIsEmpty = v.is(v.strictObject({}), claimPolicy);
	const policyIsSet = claimPolicy != null && !policyIsEmpty;
	if (claimPolicy != null && policyIsSet && claimPolicy.policyId == null) {
		claimPolicy = {
			...claimPolicy,
			policyId: -1,
		};
	}

	return {
		claimInsured: data.claimInsured,
		claim: {
			hasAlternativeContact: Object.values(alternativeContact).some(
				(v) => v != null,
			),
			dateofLoss: data.dateofLoss,
			isCat: data.isCat,
			interruptionOnGoing: data.interruptionOnGoing,
			incidentDescription: data.incidentDescription,
			estimatedPeriodofIndemnity: data.estimatedPeriodofIndemnity,
			estimatedPeriodofIndemnityDateType:
				data.estimatedPeriodofIndemnityDateType,
			calculation: data.calculationTypeId,
			catCodeId: data.catCodeId,
			causeOfLossCodeId: data.causeOfLossCodeId,
		},
		claimPolicy,
		alternativeContact: data.alternativeContact,
	};
};

export const claimOpts = formOpts<ClaimValues>();

export const FNOLForm = lazy(() => import('./FNOLForm'));
